// src/components/emergencias/ModalRegistrarEjecucion.js
import React, { useState, useEffect } from 'react';
import { X, Plus, Trash2, Save, AlertCircle, Wrench } from 'lucide-react';
import { emergenciasAPI, maquinariaAPI } from '../../services/apiService';

const ModalRegistrarEjecucion = ({ isOpen, onClose, emergencia, onSuccess }) => {
  const [maquinas, setMaquinas] = useState([]);
  const [tiposMaquina, setTiposMaquina] = useState([]);
  
  const [formData, setFormData] = useState({
    Estado: 'En Atención',
    Observaciones: ''
  });

  const [errors, setErrors] = useState({});
  const [guardando, setGuardando] = useState(false);
  const [loadingTipos, setLoadingTipos] = useState(false);

  // Cargar tipos de maquinaria al abrir el modal
  useEffect(() => {
    if (isOpen) {
      loadTiposMaquinaria();
    }
  }, [isOpen]);

  // Inicializar datos cuando se selecciona una emergencia
  useEffect(() => {
    if (isOpen && emergencia) {
      // Inicializar con máquinas existentes o vacío
      if (emergencia.Maquinas && emergencia.Maquinas.length > 0) {
        setMaquinas(emergencia.Maquinas.map(m => ({
          Id: m.Id || null,
          TipoMaquinaFK: m.TipoMaquinaFK,
          Cantidad: m.Cantidad || 0,
          TotalHorasTrabajadas: m.TotalHorasTrabajadas || 0,
          PrecioPorHora: m.PrecioPorHora || 0,
          Subtotal: (m.TotalHorasTrabajadas || 0) * (m.PrecioPorHora || 0)
        })));
      } else {
        setMaquinas([]);
      }
      
      setFormData({
        Estado: emergencia.Estado === 'Por Atender' ? 'En Atención' : emergencia.Estado,
        Observaciones: emergencia.Observaciones || ''
      });
    }
  }, [isOpen, emergencia]);

  const loadTiposMaquinaria = async () => {
    try {
      setLoadingTipos(true);
      console.log('🔧 Cargando tipos de maquinaria...');
      
      const response = await maquinariaAPI.getTipos();
      console.log('📦 Respuesta de API:', response);
      
      let tipos = [];
      
      // Manejar diferentes formatos de respuesta
      if (response.success && response.data) {
        tipos = response.data;
      } else if (Array.isArray(response)) {
        tipos = response;
      } else if (response.data && Array.isArray(response.data)) {
        tipos = response.data;
      }
      
      console.log('✅ Tipos cargados:', tipos);
      
      if (tipos.length === 0) {
        console.warn('⚠️ No se encontraron tipos de maquinaria');
        alert('No se pudieron cargar los tipos de maquinaria. Verifique la conexión con el servidor.');
      }
      
      setTiposMaquina(tipos);
    } catch (error) {
      console.error('❌ Error al cargar maquinaria:', error);
      console.error('Error completo:', error.response || error);
      alert(`Error al cargar tipos de maquinaria: ${error.message}\n\nVerifique que el servidor esté en ejecución y el endpoint /tipo esté disponible.`);
      setTiposMaquina([]);
    } finally {
      setLoadingTipos(false);
    }
  };

  const agregarMaquina = () => {
    setMaquinas([...maquinas, {
      Id: null,
      TipoMaquinaFK: '',
      Cantidad: 1,
      TotalHorasTrabajadas: 0,
      PrecioPorHora: 0,
      Subtotal: 0
    }]);
  };

  const eliminarMaquina = (index) => {
    const nuevasMaquinas = maquinas.filter((_, i) => i !== index);
    setMaquinas(nuevasMaquinas);
  };

  const actualizarMaquina = (index, campo, valor) => {
    const nuevasMaquinas = [...maquinas];
    nuevasMaquinas[index][campo] = valor;
    
    // Calcular subtotal automáticamente cuando cambian horas o precio
    if (campo === 'TotalHorasTrabajadas' || campo === 'PrecioPorHora') {
      const horas = parseFloat(nuevasMaquinas[index].TotalHorasTrabajadas) || 0;
      const precio = parseFloat(nuevasMaquinas[index].PrecioPorHora) || 0;
      nuevasMaquinas[index].Subtotal = horas * precio;
    }
    
    setMaquinas(nuevasMaquinas);
  };

  const calcularInversionTotal = () => {
    return maquinas.reduce((total, maquina) => total + (maquina.Subtotal || 0), 0);
  };

  const validarFormulario = () => {
    const nuevosErrores = {};

    if (maquinas.length === 0) {
      nuevosErrores.maquinas = 'Debe agregar al menos una máquina';
    }

    maquinas.forEach((maquina, index) => {
      if (!maquina.TipoMaquinaFK || maquina.TipoMaquinaFK === '') {
        nuevosErrores[`maquina_${index}_tipo`] = 'Seleccione un tipo de máquina';
      }
      if (!maquina.Cantidad || maquina.Cantidad <= 0) {
        nuevosErrores[`maquina_${index}_cantidad`] = 'Cantidad inválida';
      }
      if (!maquina.TotalHorasTrabajadas || maquina.TotalHorasTrabajadas <= 0) {
        nuevosErrores[`maquina_${index}_horas`] = 'Horas inválidas';
      }
      if (!maquina.PrecioPorHora || maquina.PrecioPorHora <= 0) {
        nuevosErrores[`maquina_${index}_precio`] = 'Precio inválido';
      }
      
      // Debug: mostrar valores
      console.log(`Validando máquina ${index}:`, {
        TipoMaquinaFK: maquina.TipoMaquinaFK,
        tipo: typeof maquina.TipoMaquinaFK,
        Cantidad: maquina.Cantidad,
        Horas: maquina.TotalHorasTrabajadas,
        Precio: maquina.PrecioPorHora
      });
    });

    setErrors(nuevosErrores);
    return Object.keys(nuevosErrores).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validarFormulario()) {
      return;
    }

    setGuardando(true);

    try {
      // Preparar datos para enviar a la API según el formato esperado
      const datosActualizacion = [{
        Id: emergencia.Id,
        Estado: formData.Estado,
        Observaciones: formData.Observaciones,
        Maquinas: maquinas.map(m => ({
          Id: m.Id, // Si es null, se creará nueva máquina
          TipoMaquinaFK: m.TipoMaquinaFK, // Ya viene como string del select, no necesita parseInt
          Cantidad: parseInt(m.Cantidad) || 0,
          TotalHorasTrabajadas: parseFloat(m.TotalHorasTrabajadas) || 0,
          PrecioPorHora: parseFloat(m.PrecioPorHora) || 0
        })),
        MaquinasEliminar: [] // Aquí puedes agregar IDs de máquinas a eliminar si implementas esa funcionalidad
      }];

      console.log('Enviando datos:', datosActualizacion);

      // Llamada a la API usando el método updateBatch
      const response = await emergenciasAPI.updateBatch(datosActualizacion);

      if (response.success || response.exitosas > 0) {
        alert('✅ Ejecución registrada exitosamente');
        onSuccess?.();
        handleClose();
      } else {
        throw new Error(response.message || 'Error al registrar la ejecución');
      }
    } catch (error) {
      console.error('Error al guardar:', error);
      alert('❌ Error al registrar la ejecución: ' + (error.message || 'Error desconocido'));
    } finally {
      setGuardando(false);
    }
  };

  const handleClose = () => {
    if (!guardando) {
      setMaquinas([]);
      setFormData({ Estado: 'En Atención', Observaciones: '' });
      setErrors({});
      onClose();
    }
  };

  if (!isOpen) return null;

  const inversionTotal = calcularInversionTotal();

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-5xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="bg-gradient-to-r from-[#c8a88c] to-[#d4b89e] px-6 py-5 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-white/20 rounded-lg backdrop-blur-sm">
              <Wrench className="w-6 h-6 text-white" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-white">Registrar Ejecución</h2>
              <p className="text-white/90 text-sm">
                Emergencia #{emergencia?.Id} - {emergencia?.NombreMunicipio}
              </p>
            </div>
          </div>
          <button
            onClick={handleClose}
            disabled={guardando}
            className="p-2 hover:bg-white/20 rounded-lg transition-colors disabled:opacity-50"
          >
            <X className="w-6 h-6 text-white" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-6">
          {/* Estado y Observaciones */}
          <div className="mb-6 p-4 bg-blue-50 rounded-xl border border-blue-200">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Estado de la Emergencia
                </label>
                <select
                  value={formData.Estado}
                  onChange={(e) => setFormData({ ...formData, Estado: e.target.value })}
                  className="w-full px-4 py-2.5 bg-white border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#c8a88c] focus:border-transparent"
                >
                  
                  <option value="Atendido">Atendido</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Observaciones
                </label>
                <input
                  type="text"
                  value={formData.Observaciones}
                  onChange={(e) => setFormData({ ...formData, Observaciones: e.target.value })}
                  placeholder="Detalles adicionales..."
                  className="w-full px-4 py-2.5 bg-white border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#c8a88c] focus:border-transparent"
                />
              </div>
            </div>
          </div>

          {/* Título de Maquinaria */}
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2">
              <Wrench className="w-5 h-5 text-[#c8a88c]" />
              Maquinaria Utilizada
              {loadingTipos && (
                <span className="text-sm font-normal text-gray-500">(Cargando tipos...)</span>
              )}
              {!loadingTipos && tiposMaquina.length === 0 && (
                <span className="text-sm font-normal text-red-500">(⚠️ Sin tipos disponibles)</span>
              )}
              {!loadingTipos && tiposMaquina.length > 0 && (
                <span className="text-sm font-normal text-green-600">({tiposMaquina.length} tipos disponibles)</span>
              )}
            </h3>
            <button
              type="button"
              onClick={agregarMaquina}
              disabled={loadingTipos || tiposMaquina.length === 0}
              className="flex items-center gap-2 px-4 py-2 bg-[#c8a88c] text-white rounded-lg font-medium hover:bg-[#b8987c] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Plus className="w-4 h-4" />
              Agregar Máquina
            </button>
          </div>

          {/* Alerta si no hay tipos de maquinaria */}
          {!loadingTipos && tiposMaquina.length === 0 && (
            <div className="mb-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
              <div className="flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-semibold text-yellow-800">
                    No se pudieron cargar los tipos de maquinaria
                  </p>
                  <p className="text-sm text-yellow-700 mt-1">
                    Posibles causas:
                  </p>
                  <ul className="text-xs text-yellow-600 mt-1 ml-4 list-disc">
                    <li>El servidor no está en ejecución</li>
                    <li>El endpoint /tipo no está disponible</li>
                    <li>Problemas de conexión a la red</li>
                  </ul>
                  <button
                    type="button"
                    onClick={loadTiposMaquinaria}
                    className="mt-3 px-4 py-1.5 bg-yellow-600 text-white text-sm rounded-lg hover:bg-yellow-700 transition-colors"
                  >
                    🔄 Reintentar
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Error general de máquinas */}
          {errors.maquinas && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-red-700">
              <AlertCircle className="w-5 h-5" />
              <span className="text-sm font-medium">{errors.maquinas}</span>
            </div>
          )}

          {/* Lista de Máquinas */}
          <div className="space-y-4 mb-6">
            {maquinas.length === 0 ? (
              <div className="text-center py-12 bg-gray-50 rounded-xl border-2 border-dashed border-gray-300">
                <Wrench className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                <p className="text-gray-500">No hay máquinas agregadas</p>
                <p className="text-sm text-gray-400 mt-1">Haz clic en "Agregar Máquina" para empezar</p>
              </div>
            ) : (
              maquinas.map((maquina, index) => (
                <div key={index} className="bg-gray-50 rounded-xl p-4 border border-gray-200">
                  <div className="flex items-center justify-between mb-3">
                    <span className="font-semibold text-gray-900">Máquina #{index + 1}</span>
                    <button
                      type="button"
                      onClick={() => eliminarMaquina(index)}
                      className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                      title="Eliminar"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-3">
                    {/* Tipo de Máquina */}
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">
                        Tipo de Máquina *
                      </label>
                      <select
                        value={maquina.TipoMaquinaFK}
                        onChange={(e) => actualizarMaquina(index, 'TipoMaquinaFK', e.target.value)}
                        disabled={loadingTipos || tiposMaquina.length === 0}
                        className={`w-full px-3 py-2 bg-white border rounded-lg focus:ring-2 focus:ring-[#c8a88c] focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed ${
                          errors[`maquina_${index}_tipo`] ? 'border-red-300' : 'border-gray-300'
                        }`}
                      >
                        <option value="">
                          {loadingTipos ? 'Cargando...' : tiposMaquina.length === 0 ? 'Sin tipos disponibles' : 'Seleccionar...'}
                        </option>
                        {tiposMaquina.map(tipo => (
                          <option key={tipo.Id} value={tipo.Id}>
                            {tipo.Tipo?.trim()}
                          </option>
                        ))}
                      </select>
                      {errors[`maquina_${index}_tipo`] && (
                        <span className="text-xs text-red-600 mt-1 block">{errors[`maquina_${index}_tipo`]}</span>
                      )}
                    </div>

                    {/* Cantidad */}
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">
                        Cantidad *
                      </label>
                      <input
                        type="number"
                        min="1"
                        value={maquina.Cantidad}
                        onChange={(e) => actualizarMaquina(index, 'Cantidad', e.target.value)}
                        className={`w-full px-3 py-2 bg-white border rounded-lg focus:ring-2 focus:ring-[#c8a88c] focus:border-transparent ${
                          errors[`maquina_${index}_cantidad`] ? 'border-red-300' : 'border-gray-300'
                        }`}
                      />
                      {errors[`maquina_${index}_cantidad`] && (
                        <span className="text-xs text-red-600 mt-1 block">{errors[`maquina_${index}_cantidad`]}</span>
                      )}
                    </div>

                    {/* Horas Trabajadas */}
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">
                        Horas Trabajadas *
                      </label>
                      <input
                        type="number"
                        min="0"
                        step="0.5"
                        value={maquina.TotalHorasTrabajadas}
                        onChange={(e) => actualizarMaquina(index, 'TotalHorasTrabajadas', e.target.value)}
                        placeholder="0"
                        className={`w-full px-3 py-2 bg-white border rounded-lg focus:ring-2 focus:ring-[#c8a88c] focus:border-transparent ${
                          errors[`maquina_${index}_horas`] ? 'border-red-300' : 'border-gray-300'
                        }`}
                      />
                      {errors[`maquina_${index}_horas`] && (
                        <span className="text-xs text-red-600 mt-1 block">{errors[`maquina_${index}_horas`]}</span>
                      )}
                    </div>
                  </div>

                  {/* Precio por Hora - FILA SEPARADA */}
                  <div className="mb-3">
                    <label className="block text-xs font-medium text-gray-600 mb-1">
                      Precio por Hora (L.) *
                    </label>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={maquina.PrecioPorHora}
                      onChange={(e) => actualizarMaquina(index, 'PrecioPorHora', e.target.value)}
                      placeholder="Ingrese el precio por hora"
                      className={`w-full px-3 py-2 bg-white border rounded-lg focus:ring-2 focus:ring-[#c8a88c] focus:border-transparent ${
                        errors[`maquina_${index}_precio`] ? 'border-red-300' : 'border-gray-300'
                      }`}
                    />
                    {errors[`maquina_${index}_precio`] && (
                      <span className="text-xs text-red-600 mt-1 block">{errors[`maquina_${index}_precio`]}</span>
                    )}
                  </div>

                  {/* Información de Cálculo */}
                  <div className="bg-blue-50 rounded-lg p-3 border border-blue-200">
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <span className="text-gray-600">Precio/hora:</span>
                        <div className="font-semibold text-gray-900">
                          L. {(parseFloat(maquina.PrecioPorHora) || 0).toLocaleString('es-HN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </div>
                      </div>
                      <div>
                        <span className="text-gray-600">Subtotal:</span>
                        <div className="font-bold text-lg text-gray-900">
                          L. {maquina.Subtotal.toLocaleString('es-HN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </div>
                      </div>
                    </div>
                    <div className="mt-2 text-xs text-gray-500">
                      Cálculo: {maquina.TotalHorasTrabajadas || 0} horas × L. {(parseFloat(maquina.PrecioPorHora) || 0).toFixed(2)} = L. {maquina.Subtotal.toFixed(2)}
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>

          {/* Resumen Total */}
          {maquinas.length > 0 && (
            <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl p-6 border-2 border-green-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600 mb-1">Inversión Total Calculada</p>
                  <p className="text-xs text-gray-500">{maquinas.length} máquina(s) registrada(s)</p>
                </div>
                <div className="text-right">
                  <div className="text-3xl font-bold text-green-700">
                    L. {inversionTotal.toLocaleString('es-HN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </div>
                  <p className="text-xs text-gray-600 mt-1">Lempiras</p>
                </div>
              </div>
            </div>
          )}
        </form>

        {/* Footer */}
        <div className="border-t border-gray-200 px-6 py-4 bg-gray-50 flex items-center justify-between gap-3">
          <button
            type="button"
            onClick={handleClose}
            disabled={guardando}
            className="px-6 py-2.5 border border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-100 transition-colors disabled:opacity-50"
          >
            Cancelar
          </button>
          <button
            onClick={handleSubmit}
            disabled={guardando || maquinas.length === 0}
            className="flex items-center gap-2 px-6 py-2.5 bg-[#c8a88c] text-white rounded-lg font-medium hover:bg-[#b8987c] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {guardando ? (
              <>
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                Guardando...
              </>
            ) : (
              <>
                <Save className="w-5 h-5" />
                Guardar Ejecución
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ModalRegistrarEjecucion;